import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  map,
  share,
  take,
} from 'rxjs';
import { FormFieldBase } from '../../core/form-field/form-field-base.directive';
import { AutocompleteOptions, SurveyQuestion } from '../../models';
import { AutocompleteChoice, AutocompleteService } from '../../services';
import { InputBaseComponent } from '../input-base/input-base.component';

@UntilDestroy()
@Component({
  selector: 'lib-survey-autocomplete-input',
  template: `<lib-input-base [formGroup]="innerForm">
    <input
      #input
      type="text"
      class="form-control"
      formControlName="choice"
      [matAutocomplete]="auto"
      (input)="onTextChange(input.value)"
      (focusout)="onFocusOut(input.value)"
    />
    <mat-autocomplete
      #auto="matAutocomplete"
      [displayWith]="displayFn"
      [requireSelection]="options.strict"
    >
      <mat-option
        *ngFor="let choice of filteredChoices | async"
        [value]="choice"
      >
        {{ choice.label }}
      </mat-option>
    </mat-autocomplete>
  </lib-input-base>`,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputBaseComponent,
    MatAutocompleteModule,
  ],
})
export class AutocompleteInputComponent
  extends FormFieldBase
  implements OnInit
{
  private autocomplete = inject(AutocompleteService);

  @Input({ required: true }) question!: SurveyQuestion;

  get options(): AutocompleteOptions {
    return this.question.autocomplete!;
  }

  readonly innerForm = new FormGroup({
    inputText: new FormControl(''),
    choice: new FormControl<AutocompleteChoice | string | null>(null),
  });

  readonly choices = defer(() =>
    this.autocomplete.getAutocompleteChoices(this.options.choices)
  ).pipe(
    map((choices) =>
      this.options.otherLabel
        ? [...choices, { id: 'other', label: this.options.otherLabel }]
        : choices
    ),
    share()
  );

  readonly filteredChoices = combineLatest({
    value: this.innerForm.controls.inputText.valueChanges,
    choices: this.choices,
  }).pipe(
    debounceTime(100),
    map(({ value, choices }) =>
      choices.filter(
        (choice) =>
          choice.label
            .toLowerCase()
            .includes(value?.toLocaleLowerCase() ?? '') || choice.id === 'other'
      )
    )
  );

  readonly selectedValue = this.innerForm.valueChanges.pipe(
    map(({ choice }) => {
      if (typeof choice === 'string') {
        // not strict
        return choice.length ? choice : null;
      }
      return choice?.id;
    })
  );

  override ngOnInit() {
    super.ngOnInit();

    const options = this.options;

    let initialValue = this.control.value as string | null;
    if (options.persistInMemory && !initialValue) {
      initialValue = localStorage.getItem(this.persistanceKey);
      this.control.setValue(initialValue);
    }

    if (initialValue) {
      this.selectInitialValue(initialValue);
    }

    if (options.persistInMemory) {
      this.persistInMemory();
    }

    this.watchInnerForm();
  }

  private selectInitialValue(valueId: string) {
    this.choices.pipe(untilDestroyed(this), take(1)).subscribe((choices) => {
      const choice = choices.find((choice) => choice.id === valueId);
      if (choice) {
        this.innerForm.setValue({
          inputText: choice.label,
          choice,
        });
      } else if (!this.options.strict) {
        this.innerForm.setValue({
          inputText: valueId,
          choice: valueId,
        });
      }
    });
  }

  private persistInMemory() {
    this.control.valueChanges
      .pipe(
        debounceTime(100),
        map((value) => (value === 'other' ? null : value)),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value) {
          localStorage.setItem(this.persistanceKey, value);
        } else {
          localStorage.removeItem(this.persistanceKey);
        }
      });
  }

  private watchInnerForm() {
    if (this.control.validator) {
      this.innerForm.controls.choice.addValidators(this.control.validator);
    }

    this.selectedValue
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((value) => this.control.setValue(value));
  }

  get persistanceKey() {
    return `remedee-autocomplete-${this.question.reference}`;
  }

  displayFn(choice: AutocompleteChoice | null) {
    return choice ? choice.label ?? choice : '';
  }

  onTextChange(inputText: string) {
    this.innerForm.controls.inputText.setValue(inputText);
  }

  onFocusOut(inputText: string) {
    const choice = this.innerForm.controls.choice.value;
    if (
      this.options.strict &&
      typeof choice !== 'string' &&
      inputText !== choice?.label
    ) {
      // Clear selection
      this.innerForm.setValue({
        inputText: '',
        choice: null,
      });
    }
  }
}
